import { Order, Orders } from '../../proto/generated/order'
import { callNative } from '../../util/nativeBridge'
import { GetAllOrdersRequest, TradingAccountSyncRequest, PlaceOrModifyOrdersResponse, PlaceOrModifyOrdersRequest, CancelOrdersRequest, CancelOrdersResponse } from '../../proto/generated/trading-bridge'
import api, { currentAdapter } from './axiosClient'
import store, { AppDispatch } from '../../store'
import { PositionRequest, PositionResponse } from '../../proto/generated/position'
import commonActions from '../../actions/common.actions'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import { UserStateType } from '../../reducer/user.reducer'
import { DeviceStateType } from '../../reducer/device.reducer'

export interface OrderHistoryResponse {
    orders: Order[]
}

export interface TradeHistoryResponse {
    trades: {
        tradeId: string
        fillTimestamp: number | string
        quantity: number
        averagePrice: number
    }[]
}


export const placeOrModifyOrder = async (request: PlaceOrModifyOrdersRequest): Promise<PlaceOrModifyOrdersResponse> => {
    const decodedData = await currentAdapter.placeOrModifyOrder(request)
    
    const state = store.getState()
    const isEmbedded = (state.deviceStore as DeviceStateType).isEmbedded
    if (!isEmbedded) {
        const user = (state.userStore as UserStateType)
        const tradingAccounts = user.tradingAccounts
        const tradingAccount = tradingAccounts?.find(t => t.accountId === request.tradingAccountId)
        const brokers = user.brokers
        const broker = brokers.find(b => b.code === tradingAccount?.brokerCode)
        AnalyticsManager.logEvent("Order Placed", { broker: broker?.name })
    } else {
        decodedData.orderResponse.forEach(orderResponse => {
            if (orderResponse.errorMessage && !orderResponse.successful) {
                callNative('onOrderError', orderResponse.errorMessage)
            }
        })
    }
    return decodedData
}

export const getOrders = async (args: { body: GetAllOrdersRequest }): Promise<Order[]> => {
    return await currentAdapter.getOrders(args.body)
}

export const getPositions = async (args: { body: { positionRequest: PositionRequest } }): Promise<PositionResponse> => {
    return await currentAdapter.getPositions(args.body.positionRequest)
}

export const syncOrders = async (args: { body: TradingAccountSyncRequest }): Promise<Order[]> => {
    return await currentAdapter.syncOrders(args.body)
}

export const cancelOrder = async (args: { dispatch: AppDispatch, body: CancelOrdersRequest, notify?: boolean }): Promise<CancelOrdersResponse> => {
    const responseData = await currentAdapter.cancelOrder(args.body)
    
    const notify = args.notify === undefined ? true : args.notify;
    notify && commonActions.setApiError(args.dispatch, {
        error: true,
        message: 'Order Cancelled successfully',
        errorType: 'success'
    })
    const isEmbedded = (store.getState().deviceStore as DeviceStateType).isEmbedded
    if (isEmbedded) {
        responseData.orderResponse.forEach(orderResponse => {
            if (orderResponse.errorMessage && !orderResponse.successful) {
                callNative('onOrderError', orderResponse.errorMessage)
            }
        })
    }
    return responseData
}

export const getPlacedOrder = async (params: any): Promise<Order> => {
    return await currentAdapter.getPlacedOrder(params)
}

export const getBasketPlacedOrder = async (basketId: string, accountId: string, batchId?: string): Promise<Order[]> => {
    const allOrders = await getOrders({ body: { tradingAccountId: accountId } });
    return allOrders.filter(order => order.basketId === basketId && order.batchId === batchId)
}

export const getOrderHistory = async (accountId: string, orderId: string): Promise<OrderHistoryResponse> => {
    const url = '/orders/order-history'

    const response = await api.post(url, {
        accountId,
        orderId
    })
    return response.data
}

export const getTradeHistory = async (accountId: string, orderId: string): Promise<TradeHistoryResponse> => {
    const url = '/orders/trade-history'

    const response = await api.post(url, {
        accountId,
        orderId
    })

    return response.data
}